import { bookingPriceAud, type PricedArtist, type SessionLength } from './pricing';

/**
 * What a session is worth, and on which studio day (B1, 14ym9bv8tqr / 7kv6).
 *
 * ONE rule for every money surface: the artist's envelope, the arrears lists, the
 * Day sheet's per-artist table and turnover, Reporting, and the server's day
 * rollover. Payouts used to price a session from the booking's quoted
 * `dayRateAud` alone, so walk-ins and seeded bookings (no quote) paid the artist
 * $0 while turnover read the sale — the same session disagreed with itself.
 */

/** The sale fields the rule reads (a web `SalePayment` / a sale_payments row). */
export interface TakingsSale {
  /** Session balance taken at checkout (merch excluded). */
  balanceAud: number;
  /** Deposit applied to that balance. */
  depositAppliedAud: number;
}

/** The booking fields the rule reads. */
export interface TakingsBooking {
  status: string;
  sessionLength: SessionLength;
  start: string;
  dayRateAud?: number;
  amountPaidAud?: number;
  checkedOutAt?: string;
  paidAt?: string;
}

/**
 * A completed session is worth its SALE (balance + deposit applied) — the money
 * actually rung up — else what was recorded as paid, else its booked price. Work
 * not checked out yet has no sale, so it is valued at its booked price: the day
 * rate it was struck at, else the artist's rate card (what the Floor checkout
 * will charge).
 */
export function sessionTakingsAud(
  booking: TakingsBooking,
  sale: TakingsSale | undefined,
  artist: PricedArtist | undefined,
): number {
  if (booking.status === 'completed') {
    if (sale) return sale.balanceAud + sale.depositAppliedAud;
    if (booking.amountPaidAud != null) return booking.amountPaidAud;
  }
  return bookingPriceAud(booking, artist);
}

// ── The commission engine (Prompt E2), shared by web and the server rollover ──

export type CommissionArrangement =
  | { kind: 'percentage'; artistShare: number }
  | { kind: 'owner-artist' }
  | { kind: 'flat-day-fee'; feeAud: number };

/** A single make-sale line contributing to an artist's day (or a merch line). */
export interface SaleLine {
  bookingId?: string;
  amountAud: number;
  /** Merch/aftercare carries no artist commission and is excluded from payout. */
  isMerch?: boolean;
}

/** A day's commission split for one artist. */
export interface CommissionBreakdown {
  arrangement: CommissionArrangement;
  /** Commissionable session takings (merch excluded). */
  grossAud: number;
  /** Merch/aftercare taken alongside, never part of the payout. */
  merchAud: number;
  /** The studio's share of the commissionable gross. */
  houseShareAud: number;
  /** The artist's envelope for the day. */
  payoutAud: number;
  /** Number of commissionable sessions counted. */
  sessionCount: number;
}

/**
 * Resolve the arrangement to apply. An explicit `commissionArrangement` on the
 * artist wins (the per-artist override slot); otherwise it is the percentage
 * default derived from `commissionRate`.
 */
export function arrangementForArtist(artist: {
  commissionRate: number;
  commissionArrangement?: CommissionArrangement;
}): CommissionArrangement {
  return artist.commissionArrangement ?? { kind: 'percentage', artistShare: artist.commissionRate };
}

/**
 * Compute the day's payout for one arrangement over its sale lines. Payout is
 * rounded to whole dollars — cash envelopes are counted in notes and coins, not
 * cents. `// ASSUMPTION:` whole-dollar rounding of the envelope is a demo choice;
 * the client hasn't specified rounding treatment.
 */
export function computePayout(arrangement: CommissionArrangement, lines: readonly SaleLine[]): CommissionBreakdown {
  const sessionLines = lines.filter((l) => !l.isMerch);
  const grossAud = sessionLines.reduce((sum, l) => sum + l.amountAud, 0);
  const merchAud = lines.filter((l) => l.isMerch).reduce((sum, l) => sum + l.amountAud, 0);

  let payoutAud: number;
  switch (arrangement.kind) {
    case 'percentage':
      payoutAud = Math.round(grossAud * arrangement.artistShare);
      break;
    case 'owner-artist':
      payoutAud = grossAud;
      break;
    case 'flat-day-fee':
      // Flat fee is owed only if the artist actually worked a session today.
      payoutAud = sessionLines.length > 0 ? arrangement.feeAud : 0;
      break;
  }

  return {
    arrangement,
    grossAud,
    merchAud,
    houseShareAud: grossAud - payoutAud,
    payoutAud,
    sessionCount: sessionLines.length,
  };
}
